import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  BarChart3,
  Activity,
  Target,
} from 'lucide-react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import { mapThesisTimeframeToRiskTiers } from '../tiers/tierCatalog';
import TierChip from '../components/portfolio/TierChip';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  Cell,
} from 'recharts';

const TIMEFRAMES: Array<{
  value: '1_day' | '1_2_weeks' | '1_month' | '6_months_plus';
  label: string;
}> = [
  { value: '1_day', label: '1 Day' },
  { value: '1_2_weeks', label: '1-2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '6_months_plus', label: '6 Months+' },
];

const DIRECTIONS: Array<{
  value: 'up' | 'sideways' | 'down';
  label: string;
  icon: React.ReactNode;
  color: string;
}> = [
  {
    value: 'up',
    label: 'Up',
    icon: <TrendingUp className="h-4 w-4" />,
    color: '#10b981',
  },
  {
    value: 'sideways',
    label: 'Sideways',
    icon: <Minus className="h-4 w-4" />,
    color: '#6b7280',
  },
  {
    value: 'down',
    label: 'Down',
    icon: <TrendingDown className="h-4 w-4" />,
    color: '#ef4444',
  },
];

interface PerformanceSnapshot {
  _id: string;
  thesisId: string;
  timeframe: string;
  direction: 'up' | 'sideways' | 'down';
  confidence: number;
  timestamp: string;
  currentPrices: {
    nasdaq: number;
    dow: number;
    nyse: number;
  };
  performance: {
    nasdaq?: {
      predicted: number;
      actual: number;
      deviation: number;
      deviationPercent: number;
      directionMatch: boolean;
    };
    dow?: {
      predicted: number;
      actual: number;
      deviation: number;
      deviationPercent: number;
      directionMatch: boolean;
    };
    nyse?: {
      predicted: number;
      actual: number;
      deviation: number;
      deviationPercent: number;
      directionMatch: boolean;
    };
  };
  overallMetrics: {
    directionAccuracy: number;
    averageDeviation: number;
    daysActive: number;
  };
}

interface PerformanceStats {
  totalSnapshots: number;
  uniqueTheses: number;
  averageDirectionAccuracy: number;
  averageDeviation: number;
  performanceByTimeframe: Record<
    string,
    {
      totalSnapshots: number;
      averageDirectionAccuracy: number;
      averageDeviation: number;
    }
  >;
  performanceByDirection: Record<
    string,
    {
      totalSnapshots: number;
      averageDirectionAccuracy: number;
      averageDeviation: number;
    }
  >;
}

const CONFIDENCE_THRESHOLD = 45;

const MarketThesisPerformance: React.FC = () => {
  const [overallStats, setOverallStats] = useState<PerformanceStats | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string | null>(
    null,
  );
  const [selectedDirection, setSelectedDirection] = useState<string | null>(
    null,
  );
  const [timeframeDirectionStats, setTimeframeDirectionStats] =
    useState<any>(null);
  const [snapshots, setSnapshots] = useState<PerformanceSnapshot[]>([]);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [confidenceStats, setConfidenceStats] = useState<any>(null);
  const [confidenceThresholdStats, setConfidenceThresholdStats] =
    useState<any>(null);
  const [targetAchievementStats, setTargetAchievementStats] =
    useState<any>(null);
  const [achievementFilter, setAchievementFilter] = useState<{
    timeframe: string | null;
    direction: string | null;
  }>({ timeframe: null, direction: null });

  useEffect(() => {
    loadOverallStats();
    loadConfidenceStats();
    loadTargetAchievementStats();
  }, []);

  useEffect(() => {
    if (selectedTimeframe && selectedDirection) {
      loadTimeframeDirectionData(
        selectedTimeframe,
        selectedDirection as 'up' | 'sideways' | 'down',
      );
    } else {
      setTimeframeDirectionStats(null);
      setSnapshots([]);
    }
  }, [selectedTimeframe, selectedDirection]);

  const loadOverallStats = async () => {
    try {
      setLoading(true);
      const stats = await apiService.getOverallPerformanceStats();
      setOverallStats(stats);
    } catch (error) {
      console.error('Failed to load overall stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTimeframeDirectionData = async (
    timeframe: string,
    direction: 'up' | 'sideways' | 'down',
  ) => {
    try {
      setSnapshotsLoading(true);
      const [stats, snapshotData, thresholdStats] = await Promise.all([
        apiService.getTimeframeDirectionPerformanceStats(timeframe, direction),
        apiService.getPerformanceSnapshotsByTimeframeAndDirection(
          timeframe,
          direction,
          100,
        ),
        apiService.getPerformanceStatsByConfidenceThreshold(
          CONFIDENCE_THRESHOLD,
          timeframe,
          direction,
        ),
      ]);
      setTimeframeDirectionStats(stats);
      setSnapshots(snapshotData);
      setConfidenceThresholdStats(thresholdStats);
    } catch (error) {
      console.error('Failed to load timeframe/direction data:', error);
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const loadConfidenceStats = async () => {
    try {
      const [confidenceData, thresholdData] = await Promise.all([
        apiService.getPerformanceStatsByConfidence(),
        apiService.getPerformanceStatsByConfidenceThreshold(
          CONFIDENCE_THRESHOLD,
        ),
      ]);
      setConfidenceStats(confidenceData);
      setConfidenceThresholdStats(thresholdData);
    } catch (error) {
      console.error('Failed to load confidence stats:', error);
    }
  };

  const loadTargetAchievementStats = async () => {
    try {
      const stats = await apiService.getTargetAchievementStats();
      setTargetAchievementStats(stats);
    } catch (error) {
      console.error('Failed to load target achievement stats:', error);
    }
  };

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDeviation = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!overallStats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No performance data available</p>
      </div>
    );
  }

  // Prepare chart data for overall stats
  const timeframeChartData = Object.entries(
    overallStats.performanceByTimeframe,
  ).map(([timeframe, data]) => ({
    timeframe:
      TIMEFRAMES.find((t) => t.value === timeframe)?.label || timeframe,
    accuracy: data.averageDirectionAccuracy * 100,
    deviation: data.averageDeviation,
    snapshots: data.totalSnapshots,
  }));

  const directionChartData = Object.entries(
    overallStats.performanceByDirection,
  ).map(([direction, data]) => ({
    direction:
      DIRECTIONS.find((d) => d.value === direction)?.label || direction,
    accuracy: data.averageDirectionAccuracy * 100,
    deviation: data.averageDeviation,
    snapshots: data.totalSnapshots,
  }));

  // Prepare time series data for snapshots, separated by confidence
  const allTimeSeriesData = snapshots
    .slice()
    .reverse()
    .map((snapshot) => {
      const isCounterprediction = snapshot.confidence < CONFIDENCE_THRESHOLD;
      return {
        timestamp: new Date(snapshot.timestamp).toLocaleDateString(),
        date: new Date(snapshot.timestamp),
        confidence: snapshot.confidence,
        isCounterprediction,
        directionAccuracy: isCounterprediction
          ? (1 - snapshot.overallMetrics.directionAccuracy) * 100
          : snapshot.overallMetrics.directionAccuracy * 100,
        averageDeviation: snapshot.overallMetrics.averageDeviation,
        nasdaqDeviation: snapshot.performance.nasdaq?.deviationPercent || 0,
        dowDeviation: snapshot.performance.dow?.deviationPercent || 0,
        nyseDeviation: snapshot.performance.nyse?.deviationPercent || 0,
      };
    });

  const predictionTimeSeriesData = allTimeSeriesData.filter(
    (d) => !d.isCounterprediction,
  );
  const counterpredictionTimeSeriesData = allTimeSeriesData.filter(
    (d) => d.isCounterprediction,
  );

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Thesis → tier map"
        description="Each timeframe feeds specific tier objectives. Performance here validates signals consumed by orchestration."
        thesisTimeframe="1_2_weeks"
      />
      <div className="flex flex-wrap gap-2">
        {TIMEFRAMES.map((tf) => (
          <div
            key={tf.value}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs"
          >
            <span className="font-medium text-gray-700">{tf.label}</span>
            {mapThesisTimeframeToRiskTiers(tf.value).map((t) => (
              <TierChip key={`${tf.value}-${t}`} riskTier={t} />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Market Thesis Performance
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and analyze prediction accuracy over time
          </p>
        </div>
        <button
          onClick={loadOverallStats}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>
      {/* Target Achievement Statistics */}
      {targetAchievementStats && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Target Achievement Statistics
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Track how well theses hit their target prices and directions
              </p>
            </div>
            <div className="flex gap-2">
              <select
                value={achievementFilter.timeframe || ''}
                onChange={(e) =>
                  setAchievementFilter({
                    ...achievementFilter,
                    timeframe: e.target.value || null,
                  })
                }
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Timeframes</option>
                {TIMEFRAMES.map((tf) => (
                  <option key={tf.value} value={tf.value}>
                    {tf.label}
                  </option>
                ))}
              </select>
              <select
                value={achievementFilter.direction || ''}
                onChange={(e) =>
                  setAchievementFilter({
                    ...achievementFilter,
                    direction: e.target.value || null,
                  })
                }
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Directions</option>
                {DIRECTIONS.map((dir) => (
                  <option key={dir.value} value={dir.value}>
                    {dir.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Finalized Theses Section */}
          <Card>
            <CardHeader>
              <CardTitle>Finalized Theses</CardTitle>
            </CardHeader>
            <CardContent>
              {targetAchievementStats.finalized.totalTheses > 0 ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Total Theses
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {targetAchievementStats.finalized.totalTheses}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Achievement Rate
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {targetAchievementStats.finalized.targetAchievementRate.toFixed(
                          1,
                        )}
                        %
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Avg Best Deviation
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {targetAchievementStats.finalized.averageBestDeviation.toFixed(
                          2,
                        )}
                        %
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Avg Direction Accuracy
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {(
                          targetAchievementStats.finalized
                            .averageBestDirectionAccuracy * 100
                        ).toFixed(1)}
                        %
                      </p>
                    </div>
                    {targetAchievementStats.finalized
                      .averageTimeToAchievement && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">
                          Avg Time to Achieve
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {targetAchievementStats.finalized.averageTimeToAchievement.toFixed(
                            1,
                          )}{' '}
                          days
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Achievement by Timeframe */}
                  {Object.keys(
                    targetAchievementStats.finalized.achievementByTimeframe,
                  ).length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Achievement Rate by Timeframe
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(
                            targetAchievementStats.finalized
                              .achievementByTimeframe,
                          )
                            .filter(
                              ([tf]) =>
                                !achievementFilter.timeframe ||
                                tf === achievementFilter.timeframe,
                            )
                            .map(([timeframe, data]: [string, any]) => ({
                              timeframe:
                                TIMEFRAMES.find((t) => t.value === timeframe)
                                  ?.label || timeframe,
                              achievementRate: data.achievementRate,
                              total: data.total,
                              achieved: data.achieved,
                              avgDeviation: data.averageBestDeviation,
                            }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timeframe" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip
                            formatter={(value: any, name: string) => {
                              if (name === 'achievementRate') {
                                return [
                                  `${value.toFixed(1)}%`,
                                  'Achievement Rate',
                                ];
                              }
                              if (name === 'avgDeviation') {
                                return [
                                  `${value.toFixed(2)}%`,
                                  'Avg Deviation',
                                ];
                              }
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar dataKey="achievementRate" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Achievement by Direction */}
                  {Object.keys(
                    targetAchievementStats.finalized.achievementByDirection,
                  ).length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Achievement Rate by Direction
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(
                            targetAchievementStats.finalized
                              .achievementByDirection,
                          )
                            .filter(
                              ([dir]) =>
                                !achievementFilter.direction ||
                                dir === achievementFilter.direction,
                            )
                            .map(([direction, data]: [string, any]) => ({
                              direction:
                                DIRECTIONS.find((d) => d.value === direction)
                                  ?.label || direction,
                              achievementRate: data.achievementRate,
                              total: data.total,
                              achieved: data.achieved,
                              avgDeviation: data.averageBestDeviation,
                              color:
                                DIRECTIONS.find((d) => d.value === direction)
                                  ?.color || '#6b7280',
                            }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="direction" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip
                            formatter={(value: any, name: string) => {
                              if (name === 'achievementRate') {
                                return [
                                  `${value.toFixed(1)}%`,
                                  'Achievement Rate',
                                ];
                              }
                              if (name === 'avgDeviation') {
                                return [
                                  `${value.toFixed(2)}%`,
                                  'Avg Deviation',
                                ];
                              }
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar dataKey="achievementRate">
                            {Object.entries(
                              targetAchievementStats.finalized
                                .achievementByDirection,
                            )
                              .filter(
                                ([dir]) =>
                                  !achievementFilter.direction ||
                                  dir === achievementFilter.direction,
                              )
                              .map(
                                ([direction, data]: [string, any], index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      DIRECTIONS.find(
                                        (d) => d.value === direction,
                                      )?.color || '#6b7280'
                                    }
                                  />
                                ),
                              )}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Achievement by Confidence */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Achievement Rate by Confidence Level
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <p className="text-sm font-medium text-gray-500 mb-2">
                          Predictions (≥45)
                        </p>
                        <p className="text-3xl font-bold text-blue-600">
                          {targetAchievementStats.finalized.achievementByConfidence.predictions.achievementRate.toFixed(
                            1,
                          )}
                          %
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {
                            targetAchievementStats.finalized
                              .achievementByConfidence.predictions.achieved
                          }{' '}
                          of{' '}
                          {
                            targetAchievementStats.finalized
                              .achievementByConfidence.predictions.total
                          }{' '}
                          achieved
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Avg Deviation:{' '}
                          {targetAchievementStats.finalized.achievementByConfidence.predictions.averageBestDeviation.toFixed(
                            2,
                          )}
                          %
                        </p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <p className="text-sm font-medium text-gray-500 mb-2">
                          Counterpredictions (&lt;45)
                        </p>
                        <p className="text-3xl font-bold text-orange-600">
                          {targetAchievementStats.finalized.achievementByConfidence.counterpredictions.achievementRate.toFixed(
                            1,
                          )}
                          %
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {
                            targetAchievementStats.finalized
                              .achievementByConfidence.counterpredictions
                              .achieved
                          }{' '}
                          of{' '}
                          {
                            targetAchievementStats.finalized
                              .achievementByConfidence.counterpredictions.total
                          }{' '}
                          achieved
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Avg Deviation:{' '}
                          {targetAchievementStats.finalized.achievementByConfidence.counterpredictions.averageBestDeviation.toFixed(
                            2,
                          )}
                          %
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Achievement by Index */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Achievement Rate by Index
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          {
                            index: 'NASDAQ',
                            ...targetAchievementStats.finalized
                              .achievementByIndex.nasdaq,
                          },
                          {
                            index: 'DOW',
                            ...targetAchievementStats.finalized
                              .achievementByIndex.dow,
                          },
                          {
                            index: 'NYSE',
                            ...targetAchievementStats.finalized
                              .achievementByIndex.nyse,
                          },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="index" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip
                          formatter={(value: any, name: string) => {
                            if (name === 'achievementRate') {
                              return [
                                `${value.toFixed(1)}%`,
                                'Achievement Rate',
                              ];
                            }
                            if (name === 'averageBestDeviation') {
                              return [`${value.toFixed(2)}%`, 'Avg Deviation'];
                            }
                            return [value, name];
                          }}
                        />
                        <Legend />
                        <Bar dataKey="achievementRate" fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No finalized theses with achievement tracking available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Theses Section */}
          <Card>
            <CardHeader>
              <CardTitle>Active Theses</CardTitle>
            </CardHeader>
            <CardContent>
              {targetAchievementStats.active.totalTheses > 0 ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Total Theses
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {targetAchievementStats.active.totalTheses}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Achievement Rate
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {targetAchievementStats.active.targetAchievementRate.toFixed(
                          1,
                        )}
                        %
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Avg Best Deviation
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {targetAchievementStats.active.averageBestDeviation.toFixed(
                          2,
                        )}
                        %
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-500">
                        Avg Direction Accuracy
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {(
                          targetAchievementStats.active
                            .averageBestDirectionAccuracy * 100
                        ).toFixed(1)}
                        %
                      </p>
                    </div>
                    {targetAchievementStats.active.averageTimeToAchievement && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">
                          Avg Time to Achieve
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {targetAchievementStats.active.averageTimeToAchievement.toFixed(
                            1,
                          )}{' '}
                          days
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Achievement by Timeframe */}
                  {Object.keys(
                    targetAchievementStats.active.achievementByTimeframe,
                  ).length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Achievement Rate by Timeframe
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(
                            targetAchievementStats.active
                              .achievementByTimeframe,
                          )
                            .filter(
                              ([tf]) =>
                                !achievementFilter.timeframe ||
                                tf === achievementFilter.timeframe,
                            )
                            .map(([timeframe, data]: [string, any]) => ({
                              timeframe:
                                TIMEFRAMES.find((t) => t.value === timeframe)
                                  ?.label || timeframe,
                              achievementRate: data.achievementRate,
                              total: data.total,
                              achieved: data.achieved,
                              avgDeviation: data.averageBestDeviation,
                            }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timeframe" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip
                            formatter={(value: any, name: string) => {
                              if (name === 'achievementRate') {
                                return [
                                  `${value.toFixed(1)}%`,
                                  'Achievement Rate',
                                ];
                              }
                              if (name === 'avgDeviation') {
                                return [
                                  `${value.toFixed(2)}%`,
                                  'Avg Deviation',
                                ];
                              }
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar dataKey="achievementRate" fill="#10b981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Achievement by Direction */}
                  {Object.keys(
                    targetAchievementStats.active.achievementByDirection,
                  ).length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Achievement Rate by Direction
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(
                            targetAchievementStats.active
                              .achievementByDirection,
                          )
                            .filter(
                              ([dir]) =>
                                !achievementFilter.direction ||
                                dir === achievementFilter.direction,
                            )
                            .map(([direction, data]: [string, any]) => ({
                              direction:
                                DIRECTIONS.find((d) => d.value === direction)
                                  ?.label || direction,
                              achievementRate: data.achievementRate,
                              total: data.total,
                              achieved: data.achieved,
                              avgDeviation: data.averageBestDeviation,
                              color:
                                DIRECTIONS.find((d) => d.value === direction)
                                  ?.color || '#6b7280',
                            }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="direction" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip
                            formatter={(value: any, name: string) => {
                              if (name === 'achievementRate') {
                                return [
                                  `${value.toFixed(1)}%`,
                                  'Achievement Rate',
                                ];
                              }
                              if (name === 'avgDeviation') {
                                return [
                                  `${value.toFixed(2)}%`,
                                  'Avg Deviation',
                                ];
                              }
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Bar dataKey="achievementRate">
                            {Object.entries(
                              targetAchievementStats.active
                                .achievementByDirection,
                            )
                              .filter(
                                ([dir]) =>
                                  !achievementFilter.direction ||
                                  dir === achievementFilter.direction,
                              )
                              .map(
                                ([direction, data]: [string, any], index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      DIRECTIONS.find(
                                        (d) => d.value === direction,
                                      )?.color || '#6b7280'
                                    }
                                  />
                                ),
                              )}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Achievement by Confidence */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Achievement Rate by Confidence Level
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <p className="text-sm font-medium text-gray-500 mb-2">
                          Predictions (≥45)
                        </p>
                        <p className="text-3xl font-bold text-blue-600">
                          {targetAchievementStats.active.achievementByConfidence.predictions.achievementRate.toFixed(
                            1,
                          )}
                          %
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {
                            targetAchievementStats.active
                              .achievementByConfidence.predictions.achieved
                          }{' '}
                          of{' '}
                          {
                            targetAchievementStats.active
                              .achievementByConfidence.predictions.total
                          }{' '}
                          achieved
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Avg Deviation:{' '}
                          {targetAchievementStats.active.achievementByConfidence.predictions.averageBestDeviation.toFixed(
                            2,
                          )}
                          %
                        </p>
                      </div>
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <p className="text-sm font-medium text-gray-500 mb-2">
                          Counterpredictions (&lt;45)
                        </p>
                        <p className="text-3xl font-bold text-orange-600">
                          {targetAchievementStats.active.achievementByConfidence.counterpredictions.achievementRate.toFixed(
                            1,
                          )}
                          %
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {
                            targetAchievementStats.active
                              .achievementByConfidence.counterpredictions
                              .achieved
                          }{' '}
                          of{' '}
                          {
                            targetAchievementStats.active
                              .achievementByConfidence.counterpredictions.total
                          }{' '}
                          achieved
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Avg Deviation:{' '}
                          {targetAchievementStats.active.achievementByConfidence.counterpredictions.averageBestDeviation.toFixed(
                            2,
                          )}
                          %
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Achievement by Index */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Achievement Rate by Index
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          {
                            index: 'NASDAQ',
                            ...targetAchievementStats.active.achievementByIndex
                              .nasdaq,
                          },
                          {
                            index: 'DOW',
                            ...targetAchievementStats.active.achievementByIndex
                              .dow,
                          },
                          {
                            index: 'NYSE',
                            ...targetAchievementStats.active.achievementByIndex
                              .nyse,
                          },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="index" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip
                          formatter={(value: any, name: string) => {
                            if (name === 'achievementRate') {
                              return [
                                `${value.toFixed(1)}%`,
                                'Achievement Rate',
                              ];
                            }
                            if (name === 'averageBestDeviation') {
                              return [`${value.toFixed(2)}%`, 'Avg Deviation'];
                            }
                            return [value, name];
                          }}
                        />
                        <Legend />
                        <Bar dataKey="achievementRate" fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No active theses with achievement tracking available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Overall Statistics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Total Snapshots
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {overallStats.totalSnapshots}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Target className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Unique Theses
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {overallStats.uniqueTheses}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Avg Direction Accuracy
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatPercentage(overallStats.averageDirectionAccuracy)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-8 w-8 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Avg Deviation
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatDeviation(overallStats.averageDeviation)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance by Timeframe */}
      <Card>
        <CardHeader>
          <CardTitle>Performance by Timeframe</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeframeChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timeframe" />
                <YAxis
                  yAxisId="left"
                  label={{
                    value: 'Accuracy %',
                    angle: -90,
                    position: 'insideLeft',
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: 'Deviation %',
                    angle: 90,
                    position: 'insideRight',
                  }}
                />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    if (name === 'accuracy') return `${value.toFixed(1)}%`;
                    if (name === 'deviation') return `${value.toFixed(2)}%`;
                    return value;
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="accuracy"
                  fill="#2563eb"
                  name="Direction Accuracy (%)"
                />
                <Bar
                  yAxisId="right"
                  dataKey="deviation"
                  fill="#f59e42"
                  name="Avg Deviation (%)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Performance by Direction */}
      <Card>
        <CardHeader>
          <CardTitle>Performance by Direction</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={directionChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="direction" />
                <YAxis
                  yAxisId="left"
                  label={{
                    value: 'Accuracy %',
                    angle: -90,
                    position: 'insideLeft',
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: 'Deviation %',
                    angle: 90,
                    position: 'insideRight',
                  }}
                />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    if (name === 'accuracy') return `${value.toFixed(1)}%`;
                    if (name === 'deviation') return `${value.toFixed(2)}%`;
                    return value;
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="accuracy"
                  fill="#10b981"
                  name="Direction Accuracy (%)"
                />
                <Bar
                  yAxisId="right"
                  dataKey="deviation"
                  fill="#ef4444"
                  name="Avg Deviation (%)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Predictions vs Counterpredictions */}
      {confidenceThresholdStats && (
        <Card>
          <CardHeader>
            <CardTitle>Predictions vs Counterpredictions</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Predictions (confidence ≥ {CONFIDENCE_THRESHOLD}) vs
              Counterpredictions (confidence &lt; {CONFIDENCE_THRESHOLD})
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Predictions */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded bg-blue-500"></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Predictions (≥{CONFIDENCE_THRESHOLD})
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Total Snapshots
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {confidenceThresholdStats.predictions.totalSnapshots}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Unique Theses
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {confidenceThresholdStats.predictions.uniqueTheses}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Direction Accuracy
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatPercentage(
                        confidenceThresholdStats.predictions
                          .averageDirectionAccuracy,
                      )}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Avg Deviation
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatDeviation(
                        confidenceThresholdStats.predictions.averageDeviation,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Counterpredictions */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded bg-orange-500"></div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Counterpredictions (&lt;{CONFIDENCE_THRESHOLD})
                  </h3>
                  <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                    Inverted Metrics
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Total Snapshots
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {
                        confidenceThresholdStats.counterpredictions
                          .totalSnapshots
                      }
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Unique Theses
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {confidenceThresholdStats.counterpredictions.uniqueTheses}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Success Rate (Inverted)
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatPercentage(
                        confidenceThresholdStats.counterpredictions
                          .averageDirectionAccuracy,
                      )}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-500">
                      Avg Deviation
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatDeviation(
                        confidenceThresholdStats.counterpredictions
                          .averageDeviation,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confidence vs Accuracy Correlation */}
      {confidenceStats && (
        <Card>
          <CardHeader>
            <CardTitle>Confidence vs Accuracy Correlation</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              How prediction accuracy correlates with confidence scores
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Confidence Range Chart */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Performance by Confidence Range
                  </h3>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded bg-blue-500"></div>
                      <span className="text-gray-600">
                        Predictions (≥{CONFIDENCE_THRESHOLD})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded bg-orange-500"></div>
                      <span className="text-gray-600">
                        Counterpredictions (&lt;{CONFIDENCE_THRESHOLD})
                      </span>
                    </div>
                  </div>
                </div>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={confidenceStats.confidenceRanges
                        .filter((r: any) => r.totalSnapshots > 0)
                        .map((r: any) => ({
                          ...r,
                          averageDirectionAccuracy:
                            r.averageDirectionAccuracy * 100,
                        }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis
                        yAxisId="left"
                        domain={[0, 100]}
                        label={{
                          value: 'Accuracy %',
                          angle: -90,
                          position: 'insideLeft',
                        }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        label={{
                          value: 'Deviation %',
                          angle: 90,
                          position: 'insideRight',
                        }}
                      />
                      <Tooltip
                        formatter={(value: any, name: string) => {
                          if (name === 'Direction Accuracy (%)')
                            return `${value.toFixed(1)}%`;
                          if (name === 'Avg Deviation (%)')
                            return `${value.toFixed(2)}%`;
                          return value;
                        }}
                      />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="averageDirectionAccuracy"
                        name="Direction Accuracy (%)"
                      >
                        {confidenceStats.confidenceRanges
                          .filter((r: any) => r.totalSnapshots > 0)
                          .map((entry: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                entry.isCounterprediction
                                  ? '#f97316'
                                  : '#3b82f6'
                              }
                            />
                          ))}
                      </Bar>
                      <Bar
                        yAxisId="right"
                        dataKey="averageDeviation"
                        fill="#ef4444"
                        name="Avg Deviation (%)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Drill Down: Select Timeframe and Direction */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Timeframe
                </label>
                <select
                  value={selectedTimeframe || ''}
                  onChange={(e) => setSelectedTimeframe(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Timeframes</option>
                  {TIMEFRAMES.map((tf) => (
                    <option key={tf.value} value={tf.value}>
                      {tf.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Direction
                </label>
                <select
                  value={selectedDirection || ''}
                  onChange={(e) => setSelectedDirection(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Directions</option>
                  {DIRECTIONS.map((dir) => (
                    <option key={dir.value} value={dir.value}>
                      {dir.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedTimeframe && selectedDirection && (
              <>
                {snapshotsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <LoadingSpinner />
                  </div>
                ) : timeframeDirectionStats ? (
                  <div className="space-y-6">
                    {/* Stats Summary */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">
                          Total Snapshots
                        </p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {timeframeDirectionStats.totalSnapshots}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">
                          Avg Direction Accuracy
                        </p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {formatPercentage(
                            timeframeDirectionStats.averageDirectionAccuracy,
                          )}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">
                          Avg Deviation
                        </p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {formatDeviation(
                            timeframeDirectionStats.averageDeviation,
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Predictions Time Series Chart */}
                    {predictionTimeSeriesData.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Predictions Performance Over Time (≥
                          {CONFIDENCE_THRESHOLD})
                        </h3>
                        <div className="h-96">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={predictionTimeSeriesData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="timestamp"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis
                                yAxisId="left"
                                label={{
                                  value: 'Accuracy %',
                                  angle: -90,
                                  position: 'insideLeft',
                                }}
                              />
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                label={{
                                  value: 'Deviation %',
                                  angle: 90,
                                  position: 'insideRight',
                                }}
                              />
                              <Tooltip
                                formatter={(value: any, name: string) => {
                                  if (name.includes('Accuracy'))
                                    return `${value.toFixed(1)}%`;
                                  if (name.includes('Deviation'))
                                    return `${value.toFixed(2)}%`;
                                  return value;
                                }}
                              />
                              <Legend />
                              <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="directionAccuracy"
                                stroke="#3b82f6"
                                strokeWidth={2}
                                name="Direction Accuracy (%)"
                                dot={{ r: 4 }}
                              />
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="averageDeviation"
                                stroke="#ef4444"
                                strokeWidth={2}
                                name="Avg Deviation (%)"
                                dot={{ r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Counterpredictions Time Series Chart */}
                    {counterpredictionTimeSeriesData.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Counterpredictions Performance Over Time (&lt;
                          {CONFIDENCE_THRESHOLD})
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded ml-2">
                            Inverted Metrics
                          </span>
                        </h3>
                        <div className="h-96">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={counterpredictionTimeSeriesData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="timestamp"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis
                                yAxisId="left"
                                label={{
                                  value: 'Success Rate % (Inverted)',
                                  angle: -90,
                                  position: 'insideLeft',
                                }}
                              />
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                label={{
                                  value: 'Deviation %',
                                  angle: 90,
                                  position: 'insideRight',
                                }}
                              />
                              <Tooltip
                                formatter={(value: any, name: string) => {
                                  if (name.includes('Accuracy'))
                                    return `${value.toFixed(1)}%`;
                                  if (name.includes('Deviation'))
                                    return `${value.toFixed(2)}%`;
                                  return value;
                                }}
                              />
                              <Legend />
                              <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="directionAccuracy"
                                stroke="#f97316"
                                strokeWidth={2}
                                name="Success Rate % (Inverted)"
                                dot={{ r: 4 }}
                              />
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="averageDeviation"
                                stroke="#ef4444"
                                strokeWidth={2}
                                name="Avg Deviation (%)"
                                dot={{ r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Index Performance Chart - Predictions */}
                    {predictionTimeSeriesData.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Predictions: Deviation by Index
                        </h3>
                        <div className="h-96">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={predictionTimeSeriesData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="timestamp"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis
                                label={{
                                  value: 'Deviation %',
                                  angle: -90,
                                  position: 'insideLeft',
                                }}
                              />
                              <Tooltip
                                formatter={(value: any) =>
                                  `${value.toFixed(2)}%`
                                }
                              />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="nasdaqDeviation"
                                stroke="#2563eb"
                                strokeWidth={2}
                                name="NASDAQ Deviation (%)"
                                dot={{ r: 3 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="dowDeviation"
                                stroke="#10b981"
                                strokeWidth={2}
                                name="DOW Deviation (%)"
                                dot={{ r: 3 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="nyseDeviation"
                                stroke="#f59e42"
                                strokeWidth={2}
                                name="NYSE Deviation (%)"
                                dot={{ r: 3 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Index Performance Chart - Counterpredictions */}
                    {counterpredictionTimeSeriesData.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Counterpredictions: Deviation by Index
                        </h3>
                        <div className="h-96">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={counterpredictionTimeSeriesData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="timestamp"
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis
                                label={{
                                  value: 'Deviation %',
                                  angle: -90,
                                  position: 'insideLeft',
                                }}
                              />
                              <Tooltip
                                formatter={(value: any) =>
                                  `${value.toFixed(2)}%`
                                }
                              />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="nasdaqDeviation"
                                stroke="#2563eb"
                                strokeWidth={2}
                                name="NASDAQ Deviation (%)"
                                dot={{ r: 3 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="dowDeviation"
                                stroke="#10b981"
                                strokeWidth={2}
                                name="DOW Deviation (%)"
                                dot={{ r: 3 }}
                              />
                              <Line
                                type="monotone"
                                dataKey="nyseDeviation"
                                stroke="#f59e42"
                                strokeWidth={2}
                                name="NYSE Deviation (%)"
                                dot={{ r: 3 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Per-Index Statistics */}
                    {timeframeDirectionStats.performanceByIndex && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Performance by Index
                        </h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                          {Object.entries(
                            timeframeDirectionStats.performanceByIndex,
                          ).map(([index, data]: [string, any]) => (
                            <div
                              key={index}
                              className="bg-gray-50 p-4 rounded-lg"
                            >
                              <p className="text-sm font-medium text-gray-500 mb-2">
                                {index.toUpperCase()}
                              </p>
                              <div className="space-y-2">
                                <div>
                                  <p className="text-xs text-gray-500">
                                    Avg Deviation
                                  </p>
                                  <p className="text-lg font-semibold text-gray-900">
                                    {formatDeviation(data.averageDeviation)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">
                                    Direction Match Rate
                                  </p>
                                  <p className="text-lg font-semibold text-gray-900">
                                    {formatPercentage(data.directionMatchRate)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No data available for selected timeframe and direction
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketThesisPerformance;
